import React from 'react';
import { Shield, Server, ExternalLink, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { Button } from '../components/ui/button';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/eur3r5tq_FOR%20ACP.png';

const MerchantLoginACP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="acp-login-page">
      <Header />
      <div className="h-20" />

      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="acp-hero"
      >
        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-black/80 hover:text-black font-semibold mb-8 transition-colors duration-200"
            data-testid="acp-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="mb-10 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/50 text-[#c24414] text-[11px] font-bold uppercase tracking-[0.15em] mb-5 backdrop-blur-sm shadow-sm">
              <Server size={13} />
              ACP
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-5 leading-tight" data-testid="acp-title">
              ACP (Acquiring Control Platform)
            </h1>
            <p className="text-lg text-black leading-relaxed max-w-3xl font-medium" data-testid="acp-subtitle">
              eCOMM's established system used for merchant onboarding, configuration and operational management. Support key internal processes and selected merchant activities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
            {/* Sign-in card */}
            <div className="md:col-span-2 rounded-lg bg-white/30 backdrop-blur-md border border-white/40 p-7 shadow-sm" data-testid="acp-cta-card">
              <h2 className="text-xl font-bold text-black mb-3">Sign-in to ACP Portal</h2>
              <p className="text-sm text-black/80 leading-relaxed mb-6">
                Manage merchant onboarding, configurations and operational processes. Internal users can log in here to access to legacy system functionality.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-[#1F4E79] hover:bg-[#163a5f] text-white text-base px-7 py-5"
                data-testid="acp-open-portal-btn"
              >
                <a href="https://acp.ecomm365.com/Portal/login.aspx" target="_blank" rel="noopener noreferrer">
                  Open ACP Portal
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>

              <div className="mt-7 pt-5 border-t border-black/15">
                <h3 className="text-xs font-bold text-black uppercase tracking-[0.15em] mb-3">Need help?</h3>
                <ul className="space-y-2 text-sm text-black/85">
                  <li>• Lost your credentials? Contact <a href="mailto:csr@ecomm365.com" className="text-[#1F4E79] font-semibold hover:underline">csr@ecomm365.com</a></li>
                  <li>• New merchant? Ask your account manager for ACP onboarding</li>
                </ul>
              </div>
            </div>

            {/* Secure-access card */}
            <div className="rounded-lg bg-white/30 backdrop-blur-md border border-white/40 p-6 h-fit shadow-sm" data-testid="acp-security-card">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={18} className="text-emerald-700" />
                <h3 className="font-bold text-black">Secure Access</h3>
              </div>
              <p className="text-sm text-black/85 leading-relaxed">
                The ACP portal is PCI-DSS certified and all sessions are encrypted end-to-end. Never share your credentials — eCOMM staff will never ask for your password.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default MerchantLoginACP;
